import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { postService, tagService, channelService, bookmarkService, subscriptionService } from '../api/services';
import { formatRelativeTime, truncateText, getPostPrimaryBadgeLabel, getPostPrimaryBadgeColor } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import Pagination from '../components/Pagination';
import FollowButton from '../components/FollowButton';
import './HomePage.css';

// 頻道預設文章排序對應：後台設定值 → 列表排序參數。
// custom（自定義順序）對應後端 sort=custom，依 posts.custom_rank 由大到小排列（後台「文章排序」分頁可調整）。
const CHANNEL_SORT_MAP = { newest: 'new', popular: 'hot', answered: 'answered', custom: 'custom' };

const HomePage = () => {
  const { user } = useAuth();
  const { settings } = useSettings();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [channels, setChannels] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const channelFromUrl = new URLSearchParams(location.search).get('channel') || '';

  const [filters, setFilters] = useState({
    page: 1,
    limit: 15,
    status: '',
    tag: '',
    search: '',
    channel: channelFromUrl,
    sort: 'new',
  });

  // 排序選項：最新、熱門（讚數＋留言加權）、本週最讚、已解決優先、未解決（待回答問題）
  const SORT_TABS = [
    { key: 'new', label: '最新' },
    { key: 'hot', label: '熱門' },
    { key: 'top', label: '本週最讚' },
    { key: 'answered', label: '已解決優先' },
    { key: 'unanswered', label: '未解決' },
  ];

  // 記錄「已套用過預設排序」的頻道：預設排序只在切換頻道當下套用一次，
  // 之後使用者手動點選的排序頁籤不會被頻道清單的非同步更新覆蓋回去
  const appliedSortChannelRef = useRef(null);

  // 同步網址上的頻道參數；進入頻道時套用該頻道後台設定的預設文章排序
  useEffect(() => {
    const ch = channelFromUrl ? channels.find((c) => c.id === channelFromUrl) : null;
    const shouldApplyDefault = Boolean(ch) && appliedSortChannelRef.current !== channelFromUrl;
    if (shouldApplyDefault) appliedSortChannelRef.current = channelFromUrl;
    if (!channelFromUrl) appliedSortChannelRef.current = null;
    setFilters(prev => {
      // 頻道與排序皆無需變動時原樣返回，避免觸發多餘的重新查詢
      if (prev.channel === channelFromUrl && !shouldApplyDefault) return prev;
      const next = { ...prev, channel: channelFromUrl, page: 1 };
      if (shouldApplyDefault) next.sort = CHANNEL_SORT_MAP[ch.post_sort_order] || 'new';
      return next;
    });
  }, [channelFromUrl, channels]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const apiFilters = { ...filters };
        if (!apiFilters.channel) delete apiFilters.channel;
        if (!apiFilters.status) delete apiFilters.status;
        if (!apiFilters.tag) delete apiFilters.tag;
        if (!apiFilters.search) delete apiFilters.search;

        const [postsRes, tagsRes, chRes] = await Promise.all([
          postService.list(apiFilters),
          tagService.list(),
          channelService.list(),
        ]);
        setPosts(postsRes.data);
        if (postsRes.pagination) setPagination(postsRes.pagination);
        setTags(tagsRes.data);
        setChannels(chRes.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  const currentChannel = channels.find(ch => ch.id === channelFromUrl) || null;
  const [searchInput, setSearchInput] = useState('');
  // 我的訂閱頻道集合，用於判斷目前頻道是否已追蹤
  const [subscribedChannels, setSubscribedChannels] = useState(new Set());

  useEffect(() => {
    if (!user) return;
    subscriptionService.list()
      .then((res) => {
        const channelIds = (res.data || []).filter((s) => s.target_type === 'CHANNEL').map((s) => s.target_id);
        setSubscribedChannels(new Set(channelIds));
      })
      .catch(() => { /* 靜默失敗 */ });
  }, [user]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleSearch = () => {
    handleFilterChange('search', searchInput);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  // 切換收藏狀態：以樂觀更新即時反映於畫面，避免整頁重新載入
  const handleToggleBookmark = async (e, post) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (post.is_bookmarked) {
        await bookmarkService.remove(post.id);
      } else {
        await bookmarkService.add(post.id);
      }
      setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, is_bookmarked: p.is_bookmarked ? 0 : 1 } : p)));
    } catch (err) {
      setError(err.message);
    }
  };

  const highlightKeyword = (text) => {
    if (!filters.search || !text) return text;
    const regex = new RegExp(`(${filters.search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = String(text).split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? <mark key={i} className="search-highlight">{part}</mark> : part
    );
  };

  if (loading) return <div className="container mt-5">載入中...</div>;

  return (
    <div className="home-page">
      <div className="container">
        <div className="page-header">
          <h1>{currentChannel ? currentChannel.name : settings.home_title}</h1>
          <p>{currentChannel ? currentChannel.description : settings.home_subtitle}</p>
          {user && currentChannel && (
            <div className="channel-follow">
              <FollowButton
                targetType="CHANNEL"
                targetId={currentChannel.id}
                subscribed={subscribedChannels.has(currentChannel.id)}
                onChange={(next) => setSubscribedChannels((prev) => {
                  const s = new Set(prev);
                  if (next) s.add(currentChannel.id); else s.delete(currentChannel.id);
                  return s;
                })}
              />
            </div>
          )}
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="sort-tabs">
          {SORT_TABS.map((t) => (
            <button
              key={t.key}
              className={`sort-tab ${filters.sort === t.key ? 'active' : ''}`}
              onClick={() => handleFilterChange('sort', t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="filters-section">
          <div className="search-bar">
            <input
              type="text"
              className="form-control search-input"
              placeholder="搜尋文章... (按 Enter 搜尋)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <button className="btn btn-primary search-btn" onClick={handleSearch}>🔍 搜尋</button>
            {filters.search && (
              <button className="btn btn-secondary search-clear-btn" onClick={() => { setSearchInput(''); handleFilterChange('search', ''); }}>✕</button>
            )}
          </div>

          <select
            className="form-control filter-select"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">所有狀態</option>
            <option value="OPEN">討論中</option>
            <option value="RESOLVED">已解決</option>
          </select>

          <select
            className="form-control filter-select"
            value={filters.tag}
            onChange={(e) => handleFilterChange('tag', e.target.value)}
          >
            <option value="">所有標籤</option>
            {tags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </select>
        </div>

        <div className="posts-list">
          {posts.length === 0 ? (
            <div className="empty-state">
              <p>還沒有文章，<Link to="/posts/create">發表第一篇</Link></p>
            </div>
          ) : (
            posts.map((post) => (
              <div key={post.id} className={`post-card ${post.is_pinned ? 'post-pinned' : ''}`}>
                <div className="post-header">
                  <Link to={`/posts/${post.id}`} className="post-title">
                    {post.is_pinned ? '📌 ' : ''}{highlightKeyword(post.title)}
                  </Link>
                  <div className="post-badges">
                    {post.is_pinned ? <span className="badge badge-pinned">置頂</span> : null}
                    <span className={`badge badge-${getPostPrimaryBadgeColor(post)}`}>
                      {getPostPrimaryBadgeLabel(post)}
                    </span>
                  </div>
                </div>
                <p className="post-excerpt">{highlightKeyword(truncateText(post.content, 150))}</p>
                <div className="post-meta">
                  <span className="author">作者: {post.author_name}</span>
                  <span className="time">{formatRelativeTime(post.created_at)}</span>
                  <span className="stat-pill">👍 {post.vote_count || 0}</span>
                  <span className="stat-pill">💬 {post.comment_count || 0}</span>
                  <span className="views">👁 {post.view_count}</span>
                  {user && (
                    <button
                      className={`btn-bookmark ${post.is_bookmarked ? 'bookmarked' : ''}`}
                      onClick={(e) => handleToggleBookmark(e, post)}
                      title={post.is_bookmarked ? '取消收藏' : '收藏'}
                    >
                      {post.is_bookmarked ? '★ 已收藏' : '☆ 收藏'}
                    </button>
                  )}
                  {user?.role === 'ADMIN' && (
                    <button
                      className="btn-pin"
                      onClick={(e) => {
                        e.preventDefault();
                        postService.togglePin(post.id).then(() => {
                          setFilters(prev => ({ ...prev }));
                        });
                      }}
                    >
                      {post.is_pinned ? '取消置頂' : '置頂'}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <Pagination pagination={pagination} currentPage={filters.page} onPageChange={handlePageChange} unitLabel="篇" />
      </div>
    </div>
  );
};

export default HomePage;

